import { apiClient } from '@/services/apiClient'
import type {
  HistoryDataParams,
  TimelineEvent,
  TransactionEvent,
  SaleEvent,
  RentalListingEvent,
  PermitEvent,
} from '../types'

interface Transaction {
  id: number
  date: string
  price?: number
  price_per_m2?: number
  address?: string
  buyers?: string
  sellers?: string
  transaction_area?: number
  units_nb?: number
  reason?: string
  details?: string
}

interface Listing {
  id: number
  listing_type: 'rental' | 'sale'
  price?: number
  surface?: number
  rooms?: number
  property_type?: string
  images?: string[]
  create_timestamp?: string
  expired_at?: string
  address?: string
  description?: string
  active_listing_timestamp?: string
  inactive_status_timestamp?: string
  ad_status?: string
  listing_timestamp?: string
  price_per_square_meter?: number
  living_area?: number
  land_area?: number
  rooms_nb?: number
  property_category?: string
  urls_image?: string[]
  deal_type?: string
}

interface BuildingPermit {
  id: number
  request_date?: string
  type_name?: string
  work_description?: string
  status?: string
  owner?: string
  address?: string
  inquiry_start_date?: string
  inquiry_last_date?: string
}

export const historyService = {
  /**
   * Fetch all history data in parallel
   */
  async fetchAllHistory(params: HistoryDataParams): Promise<TimelineEvent[]> {
    try {
      // Extract IDs from parameters
      const { transactionIds, listingIds, buildingPermitIds } =
        this.extractIds(params)

      // Fetch all data types in parallel for performance
      const [transactions, listings, permits] = await Promise.allSettled([
        this.fetchTransactions(transactionIds),
        this.fetchListings(listingIds),
        this.fetchBuildingPermits(buildingPermitIds),
      ])

      // Combine and transform results
      const events: TimelineEvent[] = []

      if (transactions.status === 'fulfilled') {
        events.push(...this.transformTransactions(transactions.value))
      }

      if (listings.status === 'fulfilled') {
        events.push(...this.transformListings(listings.value))
      }

      if (permits.status === 'fulfilled') {
        events.push(...this.transformPermits(permits.value))
      }

      // Sort by date descending
      return events.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      )
    } catch (error) {
      console.error('Error fetching history data:', error)
      throw error
    }
  },

  /**
   * Extract IDs from history data parameters
   */
  extractIds(params: HistoryDataParams): {
    transactionIds: string[]
    listingIds: string[]
    buildingPermitIds: string[]
  } {
    // For now, we'll work with the IDs if provided directly
    // In the future, this could be enhanced to search by location/bounds
    return {
      transactionIds: params.transactionIds || [],
      listingIds: params.listingIds || [],
      buildingPermitIds: params.buildingPermitIds || [],
    }
  },

  /**
   * Fetch transactions using GET bulk endpoint with IDs as parameters
   */
  async fetchTransactions(ids: string[]): Promise<Transaction[]> {
    if (!ids || ids.length === 0) {
      return []
    }

    try {
      // Limit to 100 IDs per request as per API constraints
      const limitedIds = ids.slice(0, 100)

      const params = new URLSearchParams()
      params.append('ids', limitedIds.join(','))
      params.append('fields', ['*'].join(','))

      const url = `/transactions/bulk?${params.toString()}`
      const response = await apiClient.get(url)
      return response.data || []
    } catch (error) {
      console.error('Error fetching transactions:', error)
      return []
    }
  },

  /**
   * Fetch listings using GET bulk endpoint with IDs as parameters
   */
  async fetchListings(ids: string[]): Promise<Listing[]> {
    if (!ids || ids.length === 0) {
      return []
    }

    try {
      // Limit to 100 IDs per request as per API constraints
      const limitedIds = ids.slice(0, 100)

      const params = new URLSearchParams()
      params.append('ids', limitedIds.join(','))
      params.append(
        'fields',
        [
          'id',
          'listing_timestamp',
          'deal_type',
          'price',
          'price_per_square_meter',
          'living_area',
          'land_area',
          'rooms_nb',
          'property_category',
          'property_type',
          'active',
          'description',
          'listing_duration',
          'address',
          'urls_image',
          'create_timestamp',
          'expired_at',
          'active_listing_timestamp',
          'inactive_status_timestamp',
          'ad_status',
        ].join(',')
      )

      const url = `/listings/bulk?${params.toString()}`
      const response = await apiClient.get(url)
      return response.data || []
    } catch (error) {
      console.error('Error fetching listings:', error)
      return []
    }
  },

  /**
   * Fetch building permits using GET bulk endpoint with IDs as parameters
   */
  async fetchBuildingPermits(ids: string[]): Promise<BuildingPermit[]> {
    if (!ids || ids.length === 0) {
      return []
    }

    try {
      // Limit to 100 IDs per request as per API constraints
      const limitedIds = ids.slice(0, 100)

      const params = new URLSearchParams()
      params.append('ids', limitedIds.join(','))
      params.append('fields', ['*'].join(','))

      const url = `/building-permits/bulk?${params.toString()}`
      const response = await apiClient.get(url)
      return response.data || []
    } catch (error) {
      console.error('Error fetching building permits:', error)
      return []
    }
  },

  /**
   * Transform transactions to timeline events
   */
  transformTransactions(transactions: Transaction[]): TransactionEvent[] {
    return transactions.map((transaction) => ({
      id: transaction.id,
      date: transaction.date,
      type: 'transaction' as const,
      title: 'Transaction',
      description: transaction.details || undefined,
      source: 'transaction' as const,
      sourceId: transaction.id,
      metadata: {
        price: transaction.price,
        pricePerM2: transaction.price_per_m2,
        address: transaction.address || '',
        buyers: transaction.buyers,
        sellers: transaction.sellers,
        area: transaction.transaction_area,
        units: transaction.units_nb,
        reason: transaction.reason,
      },
      raw: transaction,
    }))
  },

  /**
   * Transform listings to timeline events (separate sale and rental)
   */
  transformListings(listings: Listing[]): (SaleEvent | RentalListingEvent)[] {
    return listings.map((listing) => {
      const isExpired = !listing.ad_status
      const onlineDays = this.calculateOnlineDays(listing.listing_timestamp)

      const isRental =
        listing.deal_type === 'rent' || listing.listing_type === 'rental'
      const eventType = isRental ? 'rental' : 'sale'
      const eventTitle = isRental ? 'Rental Listing' : 'Sale Listing'

      const baseEvent = {
        id: `listing-${listing.id}`,
        date:
          listing.listing_timestamp ||
          listing.create_timestamp ||
          new Date().toISOString(),
        title: eventTitle,
        description: listing.description,
        status: isExpired ? 'expired' : 'active',
        source: 'listing' as const,
        sourceId: listing.id,
        metadata: {
          price: listing.price || 0,
          pricePerM2:
            listing.price_per_square_meter ||
            (listing.price && (listing.living_area || listing.land_area)
              ? listing.price / (listing.living_area || listing.land_area || 0)
              : undefined),
          area: listing.living_area || listing.land_area,
          rooms: listing.rooms_nb,
          propertyType: listing.property_type || listing.property_category,
          imageUrl: listing.urls_image?.[0],
          onlineDays,
          address: listing.address,
          description: listing.description,
        },
        raw: listing,
      }

      return {
        ...baseEvent,
        type: eventType,
      } as SaleEvent | RentalListingEvent
    })
  },

  /**
   * Transform building permits to timeline events
   */
  transformPermits(permits: BuildingPermit[]): PermitEvent[] {
    return permits.map((permit) => ({
      id: permit.id,
      date:
        permit.inquiry_start_date ||
        permit.inquiry_last_date ||
        new Date().toISOString(),
      type: 'permit' as const,
      title: 'Building Permit',
      description: permit.work_description,
      status: permit.status as any,
      source: 'permit' as const,
      sourceId: permit.id,
      metadata: {
        permitType: permit.type_name,
        workDescription: permit.work_description,
        status: permit.status,
        owner: permit.owner,
        address: permit.address,
      },
      raw: permit,
    }))
  },

  /**
   * Calculate days online for listings
   */
  calculateOnlineDays(
    createdAt?: string,
    expiredAt?: string
  ): number | undefined {
    if (!createdAt) return undefined

    const startDate = new Date(createdAt)
    const endDate = expiredAt ? new Date(expiredAt) : new Date()

    const diffTime = Math.abs(endDate.getTime() - startDate.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    return diffDays
  },

  /**
   * Export history data
   */
  async exportHistory(
    events: TimelineEvent[],
    format: 'csv' | 'excel' = 'excel'
  ) {
    const { exportToExcel, exportToCSV } = await import('../utils/exportUtils')

    if (format === 'excel') {
      exportToExcel(events)
    } else {
      exportToCSV(events)
    }
  },
}
